import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import {
  FileText,
  Stethoscope,
  Percent,
  ChevronDown,
  ChevronUp,
  CheckSquare,
  Square,
  AlertTriangle,
  Activity,
  Play,
  StopCircle,
  Save,
  Sparkles,
  Calendar,
  User
} from 'lucide-react';
import { aiAnalysisApi, parseAiJsonResponse, handleAiAnalysisError } from '../../services/aiAnalysisService';
import { useAuth } from '../../hooks/useAuth';
import {
  AiUsage,
  AiAnalysisMetadata,
  AiAnalysisJsonResponse,
  ClientAiAnalysis,
  parseAnalysisJsonForSave
} from '../../types/aiAnalysis';

interface AiAnalysisTabProps {
  clientId: number;
}

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const Container = styled.div`
  padding: 20px;
  direction: rtl;
`;

/* ========== Header ========== */
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--border-color);
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TitleIcon = styled.span`
  color: var(--primary-color);
  display: flex;
  align-items: center;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'success' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    switch (props.$variant) {
      case 'success':
        return `
          background: transparent;
          color: #16a34a;
          border: 1.5px solid #16a34a;
          &:hover:not(:disabled) {
            background: rgba(22, 163, 74, 0.08);
          }
        `;
      case 'danger':
        return `
          background: transparent;
          color: #dc2626;
          border: 1.5px solid #dc2626;
          &:hover:not(:disabled) {
            background: rgba(220, 38, 38, 0.08);
          }
        `;
      default:
        return `
          background: transparent;
          color: var(--primary-color);
          border: 1.5px solid var(--primary-color);
          &:hover:not(:disabled) {
            background: rgba(0, 115, 234, 0.08);
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MetaInfo = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 20px;
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

/* ========== Top Metric Cards ========== */
const MetricCardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.div`
  background: white;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MetricIconWrapper = styled.div<{ $bgColor: string }>`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: ${props => props.$bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MetricContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const MetricLabel = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const MetricValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.3;
`;

/* ========== Medical Summary Section ========== */
const SummarySection = styled.div`
  padding: 20px 0;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--border-color);
`;

const SummarySectionTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
`;

const SummaryText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #4b5563;
  line-height: 1.7;
`;

/* ========== Main Content Layout ========== */
const MainContentRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

/* ========== Detailed Medical Findings ========== */
const DetailedFindingsCard = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 10px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 14px 20px;
  border-bottom: 1px solid var(--border-color);
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  background: #f9fafb;
`;

const CardBody = styled.div`
  padding: 0;
`;

/* ========== Collapsible Section ========== */
const CollapsibleSection = styled.div`
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const CollapsibleHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #f9fafb;
  border: none;
  cursor: pointer;
  text-align: right;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  transition: background 0.2s ease;

  &:hover {
    background: #f3f4f6;
  }
`;

const CollapsibleIcon = styled.span`
  color: #6b7280;
  display: flex;
  align-items: center;
`;

const CollapsibleContent = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'block' : 'none'};
  padding: 16px 20px;
  background: white;
`;

/* ========== Diagnoses List ========== */
const DiagnosesList = styled.ol`
  margin: 0 0 16px 0;
  padding: 0 20px;
  list-style-type: decimal;
`;

const DiagnosisItem = styled.li`
  padding: 4px 0;
  font-size: 14px;
  color: #374151;
`;

/* ========== Eligibility Table ========== */
const EligibilityTableWrapper = styled.div`
  margin-bottom: 16px;
`;

const TableTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 8px;
  padding: 0 4px;
`;

const EligibilityTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const TableHead = styled.thead`
  background: #f3f4f6;
`;

const TableHeaderCell = styled.th`
  padding: 10px 12px;
  text-align: right;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }
`;

const TableCell = styled.td`
  padding: 10px 12px;
  color: #4b5563;
`;

const PercentageCell = styled(TableCell)`
  font-weight: 600;
  color: var(--primary-color);
  text-align: center;
`;

/* ========== Combined Explanation ========== */
const ExplanationBox = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ExplanationCard = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px;
`;

const ExplanationTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 8px;
  text-transform: uppercase;
`;

const ExplanationText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #374151;
  line-height: 1.6;
`;

/* ========== Recommendation ========== */
const RecommendationRow = styled.div`
  padding: 12px 16px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  font-size: 14px;
  color: #0369a1;
`;

const RecommendationLabel = styled.span`
  font-weight: 700;
`;

/* ========== Required Documentation Sidebar ========== */
const DocumentationCard = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 10px;
  overflow: hidden;
  height: fit-content;
`;

const DocumentationList = styled.div`
  padding: 16px 20px;
`;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
`;

const CheckboxIcon = styled.span<{ $checked: boolean }>`
  color: ${props => props.$checked ? '#16a34a' : '#d1d5db'};
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const DocumentName = styled.span`
  font-size: 14px;
  color: #374151;
`;

/* ========== Bottom Three Zones ========== */
const BottomZonesRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ZoneCard = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 10px;
  overflow: hidden;
`;

const ZoneHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  background: #f9fafb;
`;

const ZoneBody = styled.div`
  padding: 14px 16px;
`;

/* ========== Key Findings Zone ========== */
const FindingItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }
`;

const FindingIndicator = styled.div<{ $color: string }>`
  width: 4px;
  height: 100%;
  min-height: 20px;
  background: ${props => props.$color};
  border-radius: 2px;
  flex-shrink: 0;
`;

const FindingContent = styled.div`
  flex: 1;
`;

const FindingText = styled.div`
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
`;

const FindingStatus = styled.span<{ $color: string }>`
  font-size: 11px;
  color: ${props => props.$color};
  font-weight: 600;
`;

/* ========== Actions Zone ========== */
const ActionItemWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }
`;

const ActionItemText = styled.div`
  flex: 1;
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
`;

const UrgentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #fef2f2;
  color: #dc2626;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  flex-shrink: 0;
`;

/* ========== Warnings Zone ========== */
const WarningItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  background: #fefce8;
  border-radius: 6px;

  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const WarningIconWrapper = styled.div`
  color: #eab308;
  flex-shrink: 0;
  margin-top: 1px;
`;

const WarningText = styled.div`
  flex: 1;
  font-size: 13px;
  color: #713f12;
  line-height: 1.5;
`;

const NoWarningsText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #16a34a;
  font-size: 13px;
`;

/* ========== Footer Branding ========== */
const FooterBranding = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
`;

const BrandLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
`;

const BrandSubtext = styled.span`
  font-size: 11px;
  color: #9ca3af;
`;

/* ========== Loading & Empty States ========== */
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  margin-top: 16px;
  font-size: 16px;
  color: #1f2937;
`;

const LoadingSubtext = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: #6b7280;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
`;

const EmptyIcon = styled.div`
  color: #d1d5db;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
`;

const EmptyDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  max-width: 400px;
  margin: 0 auto;
`;

const ErrorBox = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ErrorText = styled.div`
  color: #dc2626;
  font-size: 14px;
`;

const ErrorDismiss = styled.button`
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
  font-size: 18px;
  padding: 4px;
`;

const StreamingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--primary-color);
  font-size: 14px;
  margin-bottom: 16px;
`;

const PulsingDot = styled.span`
  width: 8px;
  height: 8px;
  background: var(--primary-color);
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(0.8); }
  }
`;

const OldFormatNotice = styled.div`
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  color: #1f2937;

  p {
    margin: 8px 0;
    line-height: 1.6;
  }

  p:first-child {
    font-size: 16px;
    font-weight: 500;
  }

  p:last-child {
    font-size: 14px;
    color: #6b7280;
  }
`;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getImportanceColor = (importance: string): string => {
  switch (importance) {
    case 'גבוהה': return '#dc2626';
    case 'בינונית': return '#d97706';
    default: return '#16a34a';
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AiAnalysisTab: React.FC<AiAnalysisTabProps> = ({ clientId }) => {
  const { isSystemAdmin } = useAuth();
  const [usage, setUsage] = useState<AiUsage | null>(null);
  const [savedAnalysis, setSavedAnalysis] = useState<ClientAiAnalysis | null>(null);
  const [parsedJson, setParsedJson] = useState<AiAnalysisJsonResponse | null>(null);
  const [streamContent, setStreamContent] = useState<string>('');
  const [metadata, setMetadata] = useState<AiAnalysisMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Collapsible state
  const [diagnosesOpen, setDiagnosesOpen] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [usageData, analysisData] = await Promise.all([
          aiAnalysisApi.getUsage(),
          aiAnalysisApi.getLatestAnalysis(clientId)
        ]);
        setUsage(usageData);
        setSavedAnalysis(analysisData);

        if (analysisData?.analysisContent) {
          const parsed = parseAiJsonResponse(analysisData.analysisContent);
          setParsedJson(parsed);
        }
      } catch (err) {
        setError(handleAiAnalysisError(err));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [clientId]);

  // Generate analysis
  const generateAnalysis = useCallback(() => {
    if (isGenerating) {
      abortControllerRef.current?.abort();
      setIsGenerating(false);
      return;
    }

    setIsGenerating(true);
    setError(null);
    setStreamContent('');
    setParsedJson(null);
    setMetadata(null);

    abortControllerRef.current = aiAnalysisApi.streamAnalysis(
      clientId,
      (chunk) => {
        setStreamContent(prev => prev + chunk);
      },
      (meta) => {
        setMetadata(meta);
      },
      (err) => {
        setError(err);
        setIsGenerating(false);
      },
      () => {
        setIsGenerating(false);
        setStreamContent(content => {
          const parsed = parseAiJsonResponse(content);
          setParsedJson(parsed);
          return content;
        });
      }
    );
  }, [clientId, isGenerating]);

  // Save analysis
  const saveAnalysis = useCallback(async () => {
    if (!streamContent || !parsedJson) return;

    setIsSaving(true);
    setError(null);

    try {
      const saveData = parseAnalysisJsonForSave(parsedJson);
      const saved = await aiAnalysisApi.saveAnalysis({
        clientId,
        analysisContent: streamContent,
        ...saveData,
        documentsAnalyzedCount: metadata?.documentsAnalyzedCount ?? 0,
        documentsAnalyzedIds: metadata?.documentsAnalyzedIds ?? []
      });

      setSavedAnalysis(saved);
      setStreamContent('');

      const usageData = await aiAnalysisApi.getUsage();
      setUsage(usageData);
    } catch (err) {
      setError(handleAiAnalysisError(err));
    } finally {
      setIsSaving(false);
    }
  }, [clientId, streamContent, parsedJson, metadata]);

  // Render loading state
  if (isLoading) {
    return (
      <Container>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>טוען ניתוח...</LoadingText>
        </LoadingContainer>
      </Container>
    );
  }

  const displayJson = parsedJson;
  const showSaveButton = streamContent && parsedJson && !isGenerating;

  return (
    <Container>
      {/* Header */}
      <Header>
        <Title>
          <TitleIcon><Sparkles size={24} strokeWidth={2} /></TitleIcon>
          ניתוח AI - הערכת זכאות רפואית
        </Title>
        <HeaderActions>
          {isSystemAdmin && showSaveButton && (
            <ActionButton $variant="success" onClick={saveAnalysis} disabled={isSaving}>
              <Save size={16} strokeWidth={2} />
              {isSaving ? 'שומר...' : 'שמור ניתוח'}
            </ActionButton>
          )}
          {isSystemAdmin && (
            <ActionButton
              $variant={isGenerating ? 'danger' : 'primary'}
              onClick={generateAnalysis}
              disabled={usage?.limitExceeded}
            >
              {isGenerating ? (
                <>
                  <StopCircle size={16} strokeWidth={2} />
                  עצור
                </>
              ) : (
                <>
                  <Play size={16} strokeWidth={2} />
                  צור ניתוח חדש
                </>
              )}
            </ActionButton>
          )}
        </HeaderActions>
      </Header>

      {error && (
        <ErrorBox>
          <ErrorText>{error}</ErrorText>
          <ErrorDismiss onClick={() => setError(null)}>×</ErrorDismiss>
        </ErrorBox>
      )}

      {isGenerating && (
        <>
          <StreamingIndicator>
            <PulsingDot />
            מנתח מסמכים...
          </StreamingIndicator>
          {!streamContent && (
            <LoadingContainer>
              <LoadingSpinner />
              <LoadingText>מעבד מסמכי לקוח...</LoadingText>
              <LoadingSubtext>אנא המתן, תהליך זה עשוי לקחת מספר שניות</LoadingSubtext>
            </LoadingContainer>
          )}
        </>
      )}

      {savedAnalysis && !streamContent && (
        <MetaInfo>
          <MetaItem>
            <Calendar size={14} strokeWidth={2} />
            נוצר: {formatDate(savedAnalysis.createdAt)}
          </MetaItem>
          <MetaItem>
            <User size={14} strokeWidth={2} />
            יוצר: {savedAnalysis.generatedByUserName}
          </MetaItem>
          <MetaItem>
            <FileText size={14} strokeWidth={2} />
            מסמכים שנותחו: {savedAnalysis.documentsAnalyzedCount}
          </MetaItem>
        </MetaInfo>
      )}

      {displayJson ? (
        <>
          {/* Top Metric Cards */}
          <MetricCardsRow>
            <MetricCard>
              <MetricIconWrapper $bgColor="#dbeafe">
                <FileText size={24} strokeWidth={2} color="#3b82f6" />
              </MetricIconWrapper>
              <MetricContent>
                <MetricLabel>קצבה מומלצת:</MetricLabel>
                <MetricValue>{displayJson.recommendedBenefitType || 'לא זוהה'}</MetricValue>
              </MetricContent>
            </MetricCard>

            <MetricCard>
              <MetricIconWrapper $bgColor="#ccfbf1">
                <Stethoscope size={24} strokeWidth={2} color="#14b8a6" />
              </MetricIconWrapper>
              <MetricContent>
                <MetricLabel>אבחנה עיקרית:</MetricLabel>
                <MetricValue>{displayJson.diagnoses?.[0]?.name || 'לא זוהה'}</MetricValue>
              </MetricContent>
            </MetricCard>

            <MetricCard>
              <MetricIconWrapper $bgColor="#fef08a">
                <Percent size={24} strokeWidth={2} color="#ca8a04" />
              </MetricIconWrapper>
              <MetricContent>
                <MetricLabel>אחוז נכות משוער:</MetricLabel>
                <MetricValue>{displayJson.estimatedDisabilityPercentage ?? '--'}%</MetricValue>
              </MetricContent>
            </MetricCard>
          </MetricCardsRow>

          {/* Medical Status Summary */}
          {displayJson.medicalSummary && (
            <SummarySection>
              <SummarySectionTitle>סיכום מצב רפואי</SummarySectionTitle>
              <SummaryText>{displayJson.medicalSummary}</SummaryText>
            </SummarySection>
          )}

          {/* Main Content Area */}
          <MainContentRow>
            {/* Left: Detailed Medical Findings */}
            <DetailedFindingsCard>
              <CardHeader>ממצאים רפואיים מפורטים</CardHeader>
              <CardBody>
                {/* Primary Diagnoses - Collapsible */}
                <CollapsibleSection>
                  <CollapsibleHeader onClick={() => setDiagnosesOpen(!diagnosesOpen)}>
                    <span>אבחנות עיקריות</span>
                    <CollapsibleIcon>
                      {diagnosesOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </CollapsibleIcon>
                  </CollapsibleHeader>
                  <CollapsibleContent $isOpen={diagnosesOpen}>
                    {displayJson.diagnoses?.length > 0 && (
                      <DiagnosesList>
                        {displayJson.diagnoses.map((d, i) => (
                          <DiagnosisItem key={i}>{d.name}</DiagnosisItem>
                        ))}
                      </DiagnosesList>
                    )}

                    {/* Eligibility Breakdown Table */}
                    {displayJson.disabilityDetails?.relevantSections?.length > 0 && (
                      <EligibilityTableWrapper>
                        <TableTitle>טבלת פירוט זכאות</TableTitle>
                        <EligibilityTable>
                          <TableHead>
                            <tr>
                              <TableHeaderCell>סעיף</TableHeaderCell>
                              <TableHeaderCell>תיאור</TableHeaderCell>
                              <TableHeaderCell style={{ textAlign: 'center' }}>אחוז</TableHeaderCell>
                            </tr>
                          </TableHead>
                          <TableBody>
                            {displayJson.disabilityDetails.relevantSections.map((s, i) => (
                              <TableRow key={i}>
                                <TableCell>{s.section}</TableCell>
                                <TableCell>{s.description}</TableCell>
                                <PercentageCell>{s.percentage}%</PercentageCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </EligibilityTable>
                      </EligibilityTableWrapper>
                    )}

                    {/* Combined Explanation */}
                    <ExplanationBox>
                      {displayJson.disabilityDetails?.calculationExplanation && (
                        <ExplanationCard>
                          <ExplanationTitle>הסבר חישוב משולב</ExplanationTitle>
                          <ExplanationText>{displayJson.disabilityDetails.calculationExplanation}</ExplanationText>
                        </ExplanationCard>
                      )}
                      {displayJson.benefitTypeExplanation && (
                        <ExplanationCard>
                          <ExplanationTitle>הסבר סוג הקצבה</ExplanationTitle>
                          <ExplanationText>{displayJson.benefitTypeExplanation}</ExplanationText>
                        </ExplanationCard>
                      )}
                    </ExplanationBox>

                    {/* Recommendation */}
                    {displayJson.recommendedBenefitType && (
                      <RecommendationRow>
                        <RecommendationLabel>המלצה לסוג קצבה: </RecommendationLabel>
                        {displayJson.recommendedBenefitType}
                      </RecommendationRow>
                    )}
                  </CollapsibleContent>
                </CollapsibleSection>

                {/* Detailed Medical Findings - Collapsible */}
                <CollapsibleSection>
                  <CollapsibleHeader onClick={() => setDetailsOpen(!detailsOpen)}>
                    <span>פירוט ממצאים רפואיים</span>
                    <CollapsibleIcon>
                      {detailsOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </CollapsibleIcon>
                  </CollapsibleHeader>
                  <CollapsibleContent $isOpen={detailsOpen}>
                    {displayJson.diagnoses?.map((d, i) => (
                      <div key={i} style={{ marginBottom: '12px' }}>
                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>{d.name}</div>
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>{d.description}</div>
                      </div>
                    ))}
                  </CollapsibleContent>
                </CollapsibleSection>
              </CardBody>
            </DetailedFindingsCard>

            {/* Right: Required Documentation */}
            <DocumentationCard>
              <CardHeader>מסמכים נדרשים</CardHeader>
              <DocumentationList>
                {displayJson.requiredDocuments?.length > 0 ? (
                  displayJson.requiredDocuments.map((doc, i) => (
                    <DocumentItem key={i}>
                      <CheckboxIcon $checked={false}>
                        <Square size={18} strokeWidth={2} />
                      </CheckboxIcon>
                      <DocumentName>{doc.name}</DocumentName>
                    </DocumentItem>
                  ))
                ) : (
                  <DocumentItem>
                    <CheckboxIcon $checked={true}>
                      <CheckSquare size={18} strokeWidth={2} />
                    </CheckboxIcon>
                    <DocumentName>כל המסמכים הנדרשים קיימים</DocumentName>
                  </DocumentItem>
                )}
              </DocumentationList>
            </DocumentationCard>
          </MainContentRow>

          {/* Bottom Three Zones */}
          <BottomZonesRow>
            {/* Key Findings Highlights */}
            <ZoneCard>
              <ZoneHeader>ממצאים עיקריים</ZoneHeader>
              <ZoneBody>
                {displayJson.keyFindings?.length > 0 ? (
                  displayJson.keyFindings.map((f, i) => (
                    <FindingItem key={i}>
                      <FindingIndicator $color={getImportanceColor(f.importance)} />
                      <FindingContent>
                        <FindingText>{f.finding}</FindingText>
                        <FindingStatus $color={getImportanceColor(f.importance)}>
                          (חשיבות: {f.importance})
                        </FindingStatus>
                      </FindingContent>
                    </FindingItem>
                  ))
                ) : (
                  <FindingText style={{ color: '#9ca3af' }}>אין ממצאים להצגה</FindingText>
                )}
              </ZoneBody>
            </ZoneCard>

            {/* Action Recommendations */}
            <ZoneCard>
              <ZoneHeader>המלצות לפעולה</ZoneHeader>
              <ZoneBody>
                {displayJson.recommendedActions?.length > 0 ? (
                  displayJson.recommendedActions.map((a, i) => (
                    <ActionItemWrapper key={i}>
                      <ActionItemText>{a.action}</ActionItemText>
                      {a.priority === 'דחוף' && (
                        <UrgentBadge>
                          <AlertTriangle size={12} strokeWidth={2} />
                          דחוף
                        </UrgentBadge>
                      )}
                    </ActionItemWrapper>
                  ))
                ) : (
                  <FindingText style={{ color: '#9ca3af' }}>אין המלצות להצגה</FindingText>
                )}
              </ZoneBody>
            </ZoneCard>

            {/* Alerts/Warnings */}
            <ZoneCard>
              <ZoneHeader>התראות</ZoneHeader>
              <ZoneBody>
                {displayJson.warnings?.length > 0 ? (
                  displayJson.warnings.map((w, i) => (
                    <WarningItem key={i}>
                      <WarningIconWrapper>
                        <AlertTriangle size={16} strokeWidth={2} />
                      </WarningIconWrapper>
                      <WarningText>{w}</WarningText>
                    </WarningItem>
                  ))
                ) : (
                  <NoWarningsText>
                    <CheckSquare size={16} strokeWidth={2} />
                    אין התראות פעילות
                  </NoWarningsText>
                )}
              </ZoneBody>
            </ZoneCard>
          </BottomZonesRow>

          {/* Footer Branding */}
          <FooterBranding>
            <BrandLogo>
              <Activity size={18} strokeWidth={2} />
              ניתוח רפואי חכם
            </BrandLogo>
            <BrandSubtext>גרסה 2.1</BrandSubtext>
          </FooterBranding>
        </>
      ) : streamContent && !parsedJson && !isGenerating ? (
        <SummarySection>
          <SummarySectionTitle>שגיאה בפרסור התשובה</SummarySectionTitle>
          <SummaryText style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '12px', direction: 'ltr', textAlign: 'left' }}>
            {streamContent}
          </SummaryText>
          <p style={{ color: '#dc2626', marginTop: '16px', textAlign: 'center' }}>
            ה-AI לא החזיר JSON תקין. נסה שוב או פנה לתמיכה.
          </p>
        </SummarySection>
      ) : savedAnalysis && !parsedJson && !isGenerating ? (
        <OldFormatNotice>
          <p>⚠️ הניתוח הקודם נשמר בפורמט ישן שאינו תואם לתצוגה החדשה.</p>
          {isSystemAdmin && <p>לחץ על "צור ניתוח חדש" כדי ליצור ניתוח עדכני.</p>}
        </OldFormatNotice>
      ) : !isGenerating && !savedAnalysis ? (
        <EmptyState>
          <EmptyIcon>
            <Sparkles size={64} strokeWidth={1} />
          </EmptyIcon>
          <EmptyTitle>אין ניתוח AI שמור</EmptyTitle>
          <EmptyDescription>
            {isSystemAdmin
              ? 'לחץ על "צור ניתוח חדש" כדי ליצור ניתוח אוטומטי של כל המסמכים הרפואיים של הלקוח'
              : 'לא קיים ניתוח AI לצפייה עבור לקוח זה'}
          </EmptyDescription>
        </EmptyState>
      ) : null}
    </Container>
  );
};

export default AiAnalysisTab;
